import { type FC, useMemo, useState } from 'react';
import { Controller, type Resolver, useForm } from 'react-hook-form-latest';

import { _isNil, _isNotEmpty, _notNil } from '@/littledash';

import {
  type AnalysisOptions,
  type AnalysisType,
  type AverageType,
  type TrackingFrom,
} from '@/model/Analysis.model.ts';

import Button from '@/components/UI/Button';
import Checkbox from '@/components/UI/FormElements/Checkbox';
import InputWithUnits from '@/components/UI/FormElements/InputWithUnits';
import Select from '@/components/UI/Select';
import type { SelectOption } from '@/components/UI/Select/Select';

import {
  analysisDayFromOptions,
  analysisTypeOptions,
  averageTypeOptions,
} from '@/components/Studies/Groups/Analysis/Analysis.util.ts';
import {
  type AnalysisFormData,
  analysisSetupFormResolver,
} from '@/components/Studies/Groups/Analysis/AnalysisSetup.utils';

import { useAnalysis } from '@/utils/analysis/useAnalysis.tsx';

import cn from 'classnames';

import styles from './Analysis.module.scss';

interface AnalysisSetupProps {
  measurements: Record<string, { value: string; label: string }>;
  onChange: (config: AnalysisOptions) => void;
  hasAccess: boolean;
}

export const AnalysisSetup: FC<AnalysisSetupProps> = ({ measurements, onChange, hasAccess }) => {
  const { options, config } = useAnalysis();
  const resolver: Resolver<AnalysisFormData> = useMemo(() => analysisSetupFormResolver(config), [config]);
  const [saveDefault, setSaveDefault] = useState<boolean>(false);

  const {
    handleSubmit,
    control,
    formState: { isDirty, isValid },
    watch,
  } = useForm<AnalysisFormData>({
    mode: 'onChange',
    resolver,
    shouldUnregister: true,
    criteriaMode: 'all',
    defaultValues: {
      type: options?.type ?? config?.keys()?.next()?.value,
      measurement: options?.measurement,
      averageType: options?.average_type,
      day: { value: options?.day, from: options?.day_from },
      targetValue: options?.target_value,
    },
  });

  const type = watch('type') as AnalysisType;

  const typeOptions = useMemo(() => {
    return Array.from(config?.keys() ?? []).map((t) => analysisTypeOptions[t]);
  }, [config]);

  const optionData = useMemo(() => {
    const typeConfig = config?.get(type);
    return {
      measurementOptions: [...(typeConfig?.measurements ?? [])].reduce<Array<SelectOption<string>>>(
        (acc, m) => (Object.prototype.hasOwnProperty.call(measurements, m) ? [...acc, measurements[m]] : acc),
        []
      ),
      averageTypeOptions: [...(typeConfig?.average_type ?? [])].reduce<Array<SelectOption<AverageType>>>(
        (acc, at) =>
          Object.prototype.hasOwnProperty.call(averageTypeOptions, at) ? [...acc, averageTypeOptions[at]] : acc,
        []
      ),
      trackingFromOptions: [...(typeConfig?.tracking_from ?? [])].reduce<Array<SelectOption<TrackingFrom>>>(
        (acc, tf) =>
          Object.prototype.hasOwnProperty.call(analysisDayFromOptions, tf) ? [...acc, analysisDayFromOptions[tf]] : acc,
        []
      ),
    };
  }, [type, config, measurements]);

  const submit = (formData: AnalysisFormData) => {
    if (_isNil(formData.day.value)) {
      // Unreachable: resolver guarantees day.value is a number when form is valid.
      return;
    }
    onChange({
      type: formData.type,
      measurement: formData.measurement,
      day: formData.day.value,
      day_from: formData.day.from,
      average_type: formData.averageType,
      target_value: formData.targetValue,
      set_as_default: saveDefault,
    });
  };

  return (
    <form className="pa3" onSubmit={handleSubmit(submit)} data-test-component="AnalysisSetup" data-test-element="form">
      <div className="pb3" data-testid="analysis-type-container" data-test-element="type-container">
        <label>Summary Table</label>
        <Controller
          name="type"
          control={control}
          render={({ field: { value, onChange: onFieldChange, onBlur }, fieldState: { invalid } }) => (
            <Select
              className={cn({ [styles.selectIsInvalid]: invalid })}
              value={analysisTypeOptions[value]}
              onChange={(change) => onFieldChange(change?.value)}
              onBlur={onBlur}
              isMulti={false}
              options={typeOptions}
            />
          )}
        />
      </div>
      {_isNotEmpty(optionData.measurementOptions) && (
        <div className="pb3" data-test-element="measurement-container" data-testid="analysis-measurement-container">
          <label>Measurement</label>
          <Controller
            key={type}
            name="measurement"
            control={control}
            defaultValue={config?.get(type)?.measurements?.values().next().value}
            render={({ field: { value, onChange: onFieldChange, onBlur }, fieldState: { invalid } }) => (
              <Select
                className={cn({ [styles.selectIsInvalid]: invalid })}
                value={_notNil(value) ? measurements[value] : undefined}
                onChange={(change) => onFieldChange(change?.value)}
                onBlur={onBlur}
                isMulti={false}
                options={optionData.measurementOptions}
              />
            )}
          />
        </div>
      )}
      {(type === 'Oncology' || type === 'EfficacyProphylactic') && (
        <div data-test-element="target-value-container" data-testid="analysis-target-value-container">
          <label>Target tumor volume</label>
          <Controller
            name="targetValue"
            control={control}
            render={({ field: { value, onChange: onFieldChange, onBlur }, fieldState: { invalid } }) => (
              <InputWithUnits
                name="targetValue"
                hasError={invalid}
                value={value ?? undefined}
                unit="mm³"
                valueMin={0}
                valueStep={0.001}
                onChange={(change) => onFieldChange(change.value)}
                onBlur={onBlur}
              />
            )}
          />
        </div>
      )}
      {_isNotEmpty(optionData.averageTypeOptions) && (
        <div className="pb3" data-test-element="average-container" data-testid="analysis-average-container">
          <label>Summary statistic</label>
          <Controller
            key={type}
            name="averageType"
            control={control}
            defaultValue={config?.get(type)?.average_type?.values().next().value}
            render={({ field: { value, onChange: onFieldChange, onBlur }, fieldState: { invalid } }) => (
              <Select
                className={cn({ [styles.selectIsInvalid]: invalid })}
                value={_notNil(value) ? averageTypeOptions[value] : undefined}
                onChange={(change) => onFieldChange(change?.value)}
                onBlur={onBlur}
                isMulti={false}
                options={optionData.averageTypeOptions}
              />
            )}
          />
        </div>
      )}

      <div className="pb3" data-test-element="analysis-day-container" data-testid="analysis-day-container">
        <label>
          {type === 'Survival' || type === 'Tolerance' || type === 'ToleranceSurvival' ? 'Reference Date' : 'Day'}
        </label>
        {/* NOTE: intentionally no `key={type}` here — `day` must preserve its state across type
            changes so `Apply` stays enabled when switching between types that share valid
            `day.value` / `tracking_from` combinations (the resolver auto-coerces mismatches). */}
        <Controller
          name="day"
          control={control}
          defaultValue={{
            value: null,
            from: config?.get(type)?.tracking_from?.values().next().value ?? null,
          }}
          render={({ field: { value: day, onChange: onFieldChange, onBlur }, fieldState: { invalid } }) =>
            type === 'Survival' || type === 'Tolerance' || type === 'ToleranceSurvival' ? (
              <Select
                className={cn({ [styles.selectIsInvalid]: invalid })}
                value={
                  _notNil(day?.from) ? optionData.trackingFromOptions.find((option) => option.value === day.from) : null
                }
                onChange={(change) => onFieldChange({ from: change?.value, value: 1 })}
                onBlur={onBlur}
                isMulti={false}
                options={optionData.trackingFromOptions}
              />
            ) : (
              <InputWithUnits
                hasError={invalid}
                value={day?.value ?? undefined}
                unit={day?.from ?? undefined}
                valueMin={1}
                valueStep={1}
                onChange={(change) => {
                  const matchedUnit = optionData.trackingFromOptions.find((option) => option.value === change.unit);
                  onFieldChange({
                    value: _notNil(change.value) ? change.value : null,
                    from: matchedUnit?.value ?? day?.from ?? null,
                  });
                }}
                onBlur={onBlur}
                name="day"
                unitOptions={optionData.trackingFromOptions}
              />
            )
          }
        />
      </div>
      <div className="flex">
        <Button submit disabled={(!isDirty || !isValid) && !saveDefault} testId="analysis-submit-button">
          Apply
        </Button>
        {hasAccess && (
          <div className="ml3 flex items-center">
            <Checkbox
              id="set-as-default"
              name="setAsDefault"
              label="Set as default"
              labelClass="pl2"
              onChange={() => {
                setSaveDefault(!saveDefault);
              }}
              checked={saveDefault}
            />
          </div>
        )}
      </div>
    </form>
  );
};
